import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../reduxStore/store';
// import env from '../constants/env';

interface AuthState {
  user: { id: string; email: string; first_name: string; bearer_token: string } | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  successMessage: null,
};

interface LoginResponse {
  id: string;
  email: string;
  token: string;
  first_name: string;
  bearer_token: string;
}

interface LogoutResponse {
  message: string;
}

interface LoginPayload {
  username: string;
  password: string;
}

interface LogoutPayload {
  username: string;
  password: string;
}

interface ErrorResponse {
  message: string;
}

// Async thunk for login
export const loginUser = createAsyncThunk<LoginResponse, LoginPayload, { rejectValue: ErrorResponse }>(
  'auth/loginUser',
  async (userData, thunkAPI) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const shop_domain = localStorage.getItem('shop_domain');
      const response = await axios.post(`${apiUrl}${shop_domain}/api/v1/auth/login`, userData);
      const data = response.data.data;
      // console.log("These are login data email: " + data.email);
      // Store the token in localStorage
      // localStorage.setItem('accessToken', data.bearer_token);
      // console.log("Token stored: " + data.bearer_token);
      
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return thunkAPI.rejectWithValue({ message: error.response.data.message });
      }
      return thunkAPI.rejectWithValue({ message: 'An unknown error occurred' });
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk<LogoutResponse, LogoutPayload, { rejectValue: ErrorResponse }>(
  'auth/logoutUser',
  async (userData, thunkAPI) => {
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const shop_domain = localStorage.getItem('shop_domain');
      const response = await axios.post(
        `${apiUrl}${shop_domain}/api/v1/auth/logout`,
        userData,
        {
          headers: {
            'Authorization': `Bearer ${token}`  // Pass the token in the Authorization header
          }
        }
      );
      const data = response.data.data;
      console.log("Logout response message: " + data.message);
      // Remove the token from localStorage
      localStorage.removeItem('accessToken');
      console.log("Token removed");
      
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return thunkAPI.rejectWithValue({ message: error.response.data.message });
      }
      return thunkAPI.rejectWithValue({ message: 'An unknown error occurred' });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.successMessage = null;
      state.error = null;  // Clear error when logging out
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.loading = false;
        state.user = {
          id: action.payload.id,
          email: action.payload.email,
          first_name: action.payload.first_name,
          bearer_token: action.payload.bearer_token
        };
        state.successMessage = 'Login successful!';
        state.error = null;  // Ensure error is cleared on successful login
      })
      .addCase(loginUser.rejected, (state, action: PayloadAction<ErrorResponse | undefined>) => {
        state.loading = false;
        state.error = action.payload?.message || 'Unknown error';
        state.successMessage = null;  // Clear success message on error
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(logoutUser.fulfilled, (state, action: PayloadAction<LogoutResponse>) => {
        state.loading = false;
        state.user = null;
        state.successMessage = action.payload.message;
        state.error = null;  // Ensure error is cleared on successful logout
      })
      .addCase(logoutUser.rejected, (state, action: PayloadAction<ErrorResponse | undefined>) => {
        state.loading = false;
        state.error = action.payload?.message || 'Unknown error';
        state.successMessage = null;  // Clear success message on error
      });
  },
});

export const { logout, clearMessages } = authSlice.actions;

export default authSlice.reducer;
